/**
 * Convention « durée d'un tronçon » — source unique de vérité.
 *
 * `departureAt` et `arrivalAt` sont stockés en heure locale aéroport, sans offset : c'est la
 * convention aérienne et c'est ce dont l'affichage a besoin ("11h15" à Auckland, pas 00h15
 * UTC). Leur différence, en revanche, est fausse dès que le tronçon change de fuseau — sur le
 * dossier ZNLO7R, DXB→AKL sortait à 25h10 au lieu de 16h10 et AKL→DXB à 8h10 au lieu de 17h10.
 *
 * Ce module vit dans `contracts` pour que le e-ticket PDF (serveur) et le récapitulatif de
 * voyage (site client) comptent pareil.
 */
/** Offset UTC d'une date Affidium ("+04:00"), depuis `timeZone` ou à défaut la fin du `.value`. */
export function extractUtcOffset(timeZone, value) {
    if (timeZone)
        return timeZone;
    const match = value?.match(/(Z|[+-]\d{2}:\d{2})$/);
    if (!match)
        return null;
    return match[1] === 'Z' ? '+00:00' : match[1];
}
/**
 * Durée d'un tronçon en minutes, par ordre de fiabilité :
 *
 * 1. `durationMinutes`, la durée qu'Affidium nous donne et qu'on stocke désormais ;
 * 2. les offsets UTC des deux bouts, qui redonnent les instants réels ;
 * 3. la différence des heures locales, pour les dossiers enregistrés avant ces deux champs —
 *    juste tant qu'on ne change pas de fuseau, ce qui couvre l'essentiel de nos itinéraires.
 *
 * `null` quand rien n'est exploitable, pour que l'appelant affiche son propre repli plutôt
 * qu'une durée inventée.
 */
export function resolveFlightDurationMinutes(segment) {
    if (typeof segment.durationMinutes === 'number' && Number.isFinite(segment.durationMinutes)) {
        return segment.durationMinutes;
    }
    if (segment.departureUtcOffset && segment.arrivalUtcOffset) {
        const departure = Date.parse(`${segment.departureAt}${segment.departureUtcOffset}`);
        const arrival = Date.parse(`${segment.arrivalAt}${segment.arrivalUtcOffset}`);
        if (!Number.isNaN(departure) && !Number.isNaN(arrival)) {
            return Math.round((arrival - departure) / 60000);
        }
    }
    const departure = parseLocalDateTime(segment.departureAt);
    const arrival = parseLocalDateTime(segment.arrivalAt);
    if (departure === null || arrival === null)
        return null;
    // Un tronçon dont l'arrivée précède le départ n'a que deux causes : une date d'arrivée non
    // incrémentée par le fournisseur, ou un passage de fuseau vers l'ouest qu'on ne sait pas
    // lire faute d'offsets. On suppose le vol de nuit, comme le e-ticket le faisait déjà.
    const minutes = Math.round((arrival - departure) / 60000);
    return minutes < 0 ? minutes + 24 * 60 : minutes;
}
/**
 * Durée d'un tronçon, lue directement d'une réponse Affidium, en minutes.
 *
 * Pendant amont de `resolveFlightDurationMinutes` : celle-ci part de ce qu'on a stocké,
 * celle-là de ce que le fournisseur envoie, avant qu'on ne jette les offsets. On prend
 * `sector.duration.minute` quand il est là, sinon on retombe sur les `.value` bruts qui,
 * eux, portent leur offset — jamais sur `date` + `time`, dont la soustraction est fausse
 * dès qu'on change de fuseau.
 *
 * `null` quand aucune des deux sources n'est exploitable, pour que l'appelant affiche son
 * propre repli plutôt qu'une durée inventée.
 */
export function resolveSectorDurationMinutes(durationMinute, beginValue, endValue) {
    if (typeof durationMinute === 'number' && Number.isFinite(durationMinute)) {
        return durationMinute;
    }
    if (!beginValue || !endValue)
        return null;
    const begin = Date.parse(beginValue);
    const end = Date.parse(endValue);
    if (Number.isNaN(begin) || Number.isNaN(end))
        return null;
    return Math.round((end - begin) / 60000);
}
/** Minutes epoch d'un datetime lu SANS offset, tous les tronçons étant comptés pareil. */
function parseLocalDateTime(value) {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match)
        return null;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
}
